import type { ReactNode, ErrorInfo } from "react"
import React, { Component } from "react"
import { createLogger } from "@/model/logger"
import { ErrorIcon } from "../icons"

const logger = createLogger("ErrorBoundary")

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  // A changed key clears a previous error, so new content gets a fresh render
  // instead of staying stuck on the fallback for the boundary's whole lifetime.
  resetKey?: string
}

interface State {
  hasError: boolean
  error?: Error
  resetKey?: string
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, resetKey: props.resetKey }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey === state.resetKey) {
      return null
    }

    return { hasError: false, error: undefined, resetKey: props.resetKey }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("Error caught by boundary", { error, errorInfo })
    this.props.onError?.(error, errorInfo)
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className="flex items-center justify-center p-4 bg-red-50 rounded-lg">
          <div className="text-center">
            <ErrorIcon
              className="mx-auto text-red-500 mb-2"
              size={48}
            />
            <p className="text-sm text-red-700">Something went wrong</p>
            <button
              onClick={() => this.setState({ hasError: false, error: undefined })}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline"
            >
              Try again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
